import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Paper,
  TextField,
  Button,
  Divider,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip
} from "@mui/material";
import { useMediaQuery, useTheme } from "@mui/material";
import { Save as SaveIcon } from "@mui/icons-material";
import EditIcon from '@mui/icons-material/Edit';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import { getUserDetail } from "../services/callApi";
import { postUserUpdate } from "../services/callApi";
import { API_QUERY_KEYS, invalidateApiQueries } from "../utils/apiQueryCache";

function UserDetail(props) {
  const id = props.userId;
  const onBack = props.onBack;
  const onUpdated = props.onUpdated;

  // 初期値用ステート
  const [selectedUser, setSelectedUser] = useState(null); // 初期状態をnullに設定
  const [loading, setLoading] = useState(true); // ローディング状態を追加
  const [userName, setName] = useState("");
  const [furigana, setFurigana] = useState("");
  const [comment, setComment] = useState("");
  const [nameError, setNameError] = useState("");
  const [furiganaError, setFuriganaError] = useState("");
  const [selectedTab, setSelectedTab] = useState(0); // 現在選択中のタブ（初期値は第1部）
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const updateInFlightRef = useRef(false);
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  // 初期データ設定
  useEffect(() => {  
    if (!id) return;

    fetchUserDetail();
  }, [id]);
  
  useEffect(() => {
    if (selectedUser) {
      setName(selectedUser.userName || "");
      setFurigana(selectedUser.furigana || "");
    }
  }, [selectedUser]);

  const fetchUserDetail = async () => {
    try {
      console.log(`ユーザーID ${id} `);
      setLoading(true);
      const user = await getUserDetail(id);
      console.log(user[0]);
      setSelectedUser(user[0]);
    } catch (error) {
      console.error(error);
      alert("ユーザー詳細の取得に失敗しました。\nページの再読み込みをしてください。");
    } finally {
      setLoading(false);
    }
  };

  const commonStyles = {
    display: "flex",
    alignItems: "center",
    mb: 2,
  };
  
  const dividerStyles = {
    mx: 2,
    height: "1.5em",
  };
  
  const validateName = () => {
    if (!userName) {
      setNameError("未入力です");
    } else if (
      userName.length > 20 ||
      !/^[^\u0021-\u007E]+$/.test(userName) // 全角文字と半角スペースを許容
    ) {
      setNameError("全角20文字以内で入力してください");
    } else {
      setNameError("");
    }
  };
  
  const validateFurigana = () => {
    if (!furigana) {
      setFuriganaError("未入力です");
    } else if (
      furigana.length > 20 ||
      !/^[\u3041-\u3096ー\s\u0020]*$/.test(furigana) // 半角スペースを許容
    ) {
      setFuriganaError("ひらがな20文字以内で入力してください");
    } else {
      setFuriganaError("");
    }
  };


  const saveOnClick = (event) => {
    event.preventDefault(); // デフォルト動作（リロード）を防止
    validateName();
    validateFurigana();

    if (!nameError && !furiganaError) {
      updateUserData();
    }
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue); // タブを切り替える
  };

  const updateUserData = async () => {
    if (updateInFlightRef.current) {
      return;
    }

    // 送信データ
    const payload = {
      user_id: selectedUser?.userId, 
      name: userName,
      furigana: furigana,
      address: null,
      phone_number: null,
      email_address: null,
      is_active: true
    }

    updateInFlightRef.current = true;
    setIsButtonDisabled(true);
    try {
      const response = await postUserUpdate(payload);
      console.log("更新成功:", response);
      invalidateApiQueries(API_QUERY_KEYS.userDetail(id), API_QUERY_KEYS.userList);
      await onUpdated?.();
      alert("ユーザー情報が更新されました");
    } catch (error) {
      console.error("更新失敗:", error);
      alert("更新に失敗しました。もう一度試してください。");
    } finally {
      updateInFlightRef.current = false;
      setIsButtonDisabled(false);
    }
  };

  const renderTable = () => {
    // 選択中のタブに対応する回答データを取得
    const currentStatistics = selectedUser.answerStatistics[selectedTab];
    const { answers, correctAnswers, answerJudge } = currentStatistics || {};
    
    // 回答データが存在しない場合は空配列を設定
    const problems = answers?.map((userAnswer, index) => ({
      number: index + 1,               // 問題番号
      judge: answerJudge[index],  // 正誤判定
      userAnswer: userAnswer,          // ユーザーの解答
      correct: correctAnswers[index]        // 正答
    })) || [];
  
    return (
      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table size={isMdUp ? 'medium' : 'small'} sx={{ minWidth: { xs: 560, sm: 700, md: 'auto' } }}>
          <TableHead>
            <TableRow>
              <TableCell>問題番号</TableCell>
              <TableCell>正誤判定</TableCell>
              <TableCell>ユーザーの解答</TableCell>
              <TableCell>正答</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {problems.map((problem, index) => (
              <TableRow key={index}>
                <TableCell>{problem.number}</TableCell>
                <TableCell>{problem.judge === 0 ? "◯" : "×"}</TableCell>
                <TableCell>{problem.userAnswer === 0 ? "" : problem.userAnswer}</TableCell>
                <TableCell>{problem.correct}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };
  

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
    {loading ? (
      // ローディング中に表示する内容
      <Box display="flex"
           justifyContent="center"
           alignItems="center"
      >
        <CircularProgress />
      </Box>
    ) : selectedUser ? (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              mb: 2,
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 1, sm: 0 }
            }}
          >
            <Typography sx={{ fontSize: { xs: '2rem', sm: '2rem' }, fontWeight: "bold" }}>
              <EditIcon />
              ユーザー詳細
              <Box
                component="span"
                sx={{
                  fontWeight: "400",
                  fontSize: { xs: "1rem", sm: "1rem" },
                  display: { xs: 'block', sm: 'inline' },
                  ml: { xs: 0, sm: 1 },
                }}
              >
                （登録日時：{selectedUser?.registeredAt} / 解答終了時間：{selectedUser?.answerStatistics?.[0]?.updatedAt}）
              </Box>
            </Typography>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mt: { xs: 1, sm: 0 } }}>
              <Link
                underline="hover"
                color="inherit"
                sx={{ cursor: "pointer" }}
                onClick={(e) => { e.preventDefault(); onBack && onBack(); }}
              >
                ユーザー一覧
              </Link>
              <Typography color="text.primary">詳細</Typography>
            </Breadcrumbs>
          </Box>

          <Paper sx={{ p: { xs: 2, sm: 3 }, mt: 2 }} elevation={3}>
            <form>
              {/* ID */}
              <Box sx={{ ...commonStyles, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Typography variant="subtitle1" sx={{ width: { xs: '100%', sm: '30%', md: '20%' }, fontWeight: { xs: 'bold', sm: 'normal' } }}>
                  ID
                </Typography>
                <Divider orientation="vertical" flexItem sx={{ ...dividerStyles, display: { xs: 'none', sm: 'block' } }} />
                <Typography variant="body1" sx={{ width: { xs: '100%', sm: '70%', md: '80%' }, mt: { xs: 0.5, sm: 0 } }}>
                  {selectedUser?.userId}
                </Typography>
              </Box>

              {/* 受験元会社 */}
              <Box sx={{ ...commonStyles, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Typography variant="subtitle1" sx={{ width: { xs: '100%', sm: '30%', md: '20%' }, fontWeight: { xs: 'bold', sm: 'normal' } }}>
                  受験元会社
                </Typography>
                <Divider orientation="vertical" flexItem sx={{ ...dividerStyles, display: { xs: 'none', sm: 'block' } }} />
                <Typography variant="body1" sx={{ width: { xs: '100%', sm: '70%', md: '80%' }, mt: { xs: 0.5, sm: 0 } }}>
                  {/* 会社が紐づかないユーザーはLEFT JOINでnullになるため、管理者一覧と同じ表記で埋める。 */}
                  {selectedUser?.companyName ?? "－"}
                </Typography>
              </Box>

              {/* 氏名 */}
              <Box sx={{ ...commonStyles, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Typography variant="subtitle1" sx={{ width: { xs: '100%', sm: '30%', md: '20%' }, fontWeight: { xs: 'bold', sm: 'normal' } }}>
                  氏名
                </Typography>
                <Divider orientation="vertical" flexItem sx={{ ...dividerStyles, display: { xs: 'none', sm: 'block' } }} />
                <TextField
                  variant="standard"
                  sx={{ flexGrow: 1, width: { xs: '100%', sm: '70%', md: '80%' }, mt: { xs: 0.5, sm: 0 } }}
                  value={userName}
                  onChange={(e) => setName(e.target.value)}
                  onBlur={validateName}
                  error={!!nameError}
                  helperText={nameError}
                />
              </Box>

              {/* ふりがな */}
              <Box sx={{ ...commonStyles, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Typography variant="subtitle1" sx={{ width: { xs: '100%', sm: '30%', md: '20%' }, fontWeight: { xs: 'bold', sm: 'normal' } }}>
                  ふりがな
                </Typography>
                <Divider orientation="vertical" flexItem sx={{ ...dividerStyles, display: { xs: 'none', sm: 'block' } }} />
                <TextField
                  variant="standard"
                  sx={{ flexGrow: 1, width: { xs: '100%', sm: '70%', md: '80%' }, mt: { xs: 0.5, sm: 0 } }}
                  value={furigana}
                  onChange={(e) => setFurigana(e.target.value)}
                  onBlur={validateFurigana}
                  error={!!furiganaError}
                  helperText={furiganaError}
                />
              </Box>

              {/* 点数 */}
              <Box sx={{ ...commonStyles, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Typography variant="subtitle1" sx={{ width: { xs: '100%', sm: '30%', md: '20%' }, fontWeight: { xs: 'bold', sm: 'normal' } }}>
                  点数
                </Typography>
                <Divider orientation="vertical" flexItem sx={{ mx: 2, display: { xs: 'none', sm: 'block' } }} />
                <Box sx={{ width: { xs: '100%', sm: '70%', md: '80%' } }}>
                  {isMdUp ? (
                    <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                      <Table size="small" sx={{ minWidth: 560 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell></TableCell>
                            <TableCell align="right">解答数</TableCell>
                            <TableCell align="right">正答数</TableCell>
                            <TableCell align="right">正答率</TableCell>
                            <TableCell align="right">解答時間</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedUser.answerStatistics.map((answer) => {
                            const rate = answer.answerCount > 0 ? Math.round((answer.correctCount / answer.answerCount) * 100) : 0;
                            return (
                              <TableRow key={answer.questionPart}>
                                <TableCell>{`第${answer.questionPart}部`}</TableCell>
                                <TableCell align="right">{answer.answerCount}</TableCell>
                                <TableCell align="right">{answer.correctCount}</TableCell>
                                <TableCell align="right">{`${rate}%`}</TableCell>
                                <TableCell align="right">{answer.answerTime}</TableCell>
                              </TableRow>
                            );
                          })}
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>合計</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{selectedUser?.totalAnswer}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{selectedUser?.totalScore}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                              {selectedUser?.totalAnswer > 0
                                ? `${Math.round((selectedUser.totalScore / selectedUser.totalAnswer) * 100)}%`
                                : '0%'}
                            </TableCell>
                            <TableCell align="right">-</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ display: 'grid', rowGap: 1.5 }}>
                      {selectedUser.answerStatistics.map((answer) => {
                        const rate = answer.answerCount > 0 ? Math.round((answer.correctCount / answer.answerCount) * 100) : 0;
                        const color = rate >= 80 ? 'success' : rate >= 50 ? 'warning' : 'error';
                        return (
                          <Box key={answer.questionPart} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            <Typography sx={{ fontWeight: 'bold', mb: 0.5 }}>{`第${answer.questionPart}部`}</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                              <Typography>解答数: {answer.answerCount}</Typography>
                              <Typography>正答数: {answer.correctCount}</Typography>
                              <Chip label={`正答率 ${rate}%`} color={color} size="small" />
                              <Typography>解答時間: {answer.answerTime}</Typography>
                            </Box>
                          </Box>
                        );
                      })}
                      <Box sx={{ p: 1.5, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                        <Typography sx={{ fontWeight: 'bold', mb: 0.5 }}>合計</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                          <Typography>解答数: {selectedUser?.totalAnswer}</Typography>
                          <Typography>正答数: {selectedUser?.totalScore}</Typography>
                          <Chip
                            label={`正答率 ${selectedUser?.totalAnswer > 0 ? Math.round((selectedUser.totalScore / selectedUser.totalAnswer) * 100) : 0}%`}
                            size="small"
                            color={(selectedUser?.totalAnswer > 0 && Math.round((selectedUser.totalScore / selectedUser.totalAnswer) * 100) >= 80)
                              ? 'success'
                              : (selectedUser?.totalAnswer > 0 && Math.round((selectedUser.totalScore / selectedUser.totalAnswer) * 100) >= 50)
                                ? 'warning'
                                : 'error'}
                          />
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* コメント */}
              <Box sx={{ ...commonStyles, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Typography variant="subtitle1" sx={{ width: { xs: '100%', sm: '30%', md: '20%' }, fontWeight: { xs: 'bold', sm: 'normal' } }}>
                  コメント
                </Typography>
                <Divider orientation="vertical" flexItem sx={{ mx: 2, display: { xs: 'none', sm: 'block' } }} />
                <TextField
                  label="コメントを入力"
                  variant="outlined"
                  sx={{ width: { xs: '100%', sm: '70%', md: '80%' }, mt: { xs: 0.5, sm: 0 } }}
                  fullWidth
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
              </Box>

              {/* 保存ボタン */}
              <Box sx={{ mt: 3, textAlign: "center" }}>
                <Button
                  onClick={saveOnClick}
                  disabled={isButtonDisabled}
                  variant="contained"
                  color="primary"
                  type="submit"
                  startIcon={<SaveIcon />}
                >
                  保存
                </Button>
              </Box>
            </form>

            <h1>
              <Typography>
                <QuestionAnswerIcon />
                ユーザーの試験回答
              </Typography>
            </h1>
            {/* タブとテーブル */}
            <Box sx={{ mt: 4 }}>
              <Tabs
                value={selectedTab}
                onChange={handleTabChange}
                variant={isMdUp ? 'standard' : 'scrollable'}
                scrollButtons={isMdUp ? false : 'auto'}
                allowScrollButtonsMobile={!isMdUp}
                centered={isMdUp}
              >
                {selectedUser.answerStatistics.map((answer, index) => (
                  <Tab
                    key={index}
                    label={`第${answer.questionPart}部 (正答数${answer.correctCount})`}
                  />
                ))}
              </Tabs>
              {renderTable()}
            </Box>
          </Paper>
        </>
      ) : (
        <Typography>指定されたユーザーが見つかりません。</Typography>
      )}
    </Box>
  );
}

export default UserDetail;





